/**
 * StandardText List Page
 * List and manage standard texts
 *
 * @version 2025.1.0
 */
import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  FormControl,
  InputAdornment,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  TextField,
  Typography,
  Alert,
} from '@mui/material';
import {
  Add as AddIcon,
  Search as SearchIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Restore as RestoreIcon,
} from '@mui/icons-material';
import { PageShell, DataTable, type DataTableColumn } from '../../components';
import { standardTextService } from '../../services/standardTextService';
import {
  DocumentSectionCatalog,
  StandardText,
  StandardTextCategory,
  StandardTextStatus,
} from '../../types/standardText';
import { pode } from '../../authorization/pode';
import { useAcao, useControle } from '../../authorization/useVeredito';
import { RouteActionButton } from '../../components/common';

const textColumns: DataTableColumn<StandardText>[] = [
  { id: 'title', label: 'Título', primary: true, render: (text) => text.title },
  { id: 'category', label: 'Categoria', render: (text) => text.category },
  {
    id: 'scope',
    label: 'Escopo',
    render: (text) => (
      <Chip
        label={text.isGlobal ? 'Global' : text.companyName ?? 'Empresa'}
        size="small"
        color={text.isGlobal ? 'primary' : 'default'}
      />
    ),
  },
  {
    id: 'status',
    label: 'Status',
    render: (text) => (
      <Chip
        label={text.status}
        size="small"
        color={text.status === 'ATIVO' ? 'success' : 'default'}
      />
    ),
  },
];

export const StandardTextListPage: React.FC = () => {
  const navigate = useNavigate();
  const editar = useControle('/standard-texts/:id/edit', pode('standard_text.update'), 'Editar');
  const excluir = useAcao('standard_text.delete', 'Excluir');
  const reativar = useAcao('standard_text.update', 'Reativar');
  const [texts, setTexts] = useState<StandardText[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [appliedSearch, setAppliedSearch] = useState('');
  const [category, setCategory] = useState('');
  // Inativar é o "excluir" desta tela, e sem um lugar para ver os inativos ele
  // não tinha desfazer (#395). Ativos por padrão: é a lista de trabalho.
  const [status, setStatus] = useState<StandardTextStatus>(StandardTextStatus.ATIVO);
  const [reactivating, setReactivating] = useState<number | null>(null);
  const [categories, setCategories] = useState<Array<{ value: string; label: string }>>([]);
  const [documents, setDocuments] = useState<DocumentSectionCatalog[]>([]);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [total, setTotal] = useState(0);

  const loadTexts = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await standardTextService.getStandardTexts({
        page: page + 1,
        limit: rowsPerPage,
        search: appliedSearch,
        category: category ? (category as StandardTextCategory) : undefined,
        status,
      });
      setTexts(response.texts);
      setTotal(response.total);
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : 'Erro ao carregar textos padrão');
    } finally {
      setLoading(false);
    }
  }, [page, rowsPerPage, appliedSearch, category, status]);

  useEffect(() => {
    loadTexts();
  }, [loadTexts]);

  useEffect(() => {
    standardTextService
      .getCategories()
      .then(setCategories)
      .catch(() => setCategories([]));
    standardTextService
      .getDocumentSections()
      .then(setDocuments)
      .catch(() => setDocuments([]));
  }, []);

  const handleSearch = () => {
    setPage(0);
    setAppliedSearch(searchTerm);
  };

  const handleClearFilters = () => {
    setSearchTerm('');
    setAppliedSearch('');
    setCategory('');
    setStatus(StandardTextStatus.ATIVO);
    setPage(0);
  };

  const handleDelete = async (id: number) => {
    if (!window.confirm('Tem certeza que deseja excluir este texto?')) {
      return;
    }

    try {
      await standardTextService.deleteStandardText(id);
      loadTexts();
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : 'Erro ao excluir texto');
    }
  };

  // O endpoint alterna o status, então um segundo clique antes de a lista
  // recarregar inativaria o texto de novo — e a linha já teria sumido da tela,
  // deixando a pessoa certa de que reativou.
  const handleReactivate = async (id: number) => {
    if (reactivating !== null) return;

    try {
      setReactivating(id);
      await standardTextService.toggleStandardTextStatus(id);
      await loadTexts();
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : 'Erro ao reativar texto');
    } finally {
      setReactivating(null);
    }
  };

  const hasFilters = Boolean(appliedSearch || category) || status !== StandardTextStatus.ATIVO;

  // A categoria filtrada pertence a um documento? Só então dá para nomear seções.
  const document = useMemo(
    () => documents.find((doc) => doc.sections.some((section) => section.category === category)),
    [documents, category]
  );

  const sectionsWithoutText = useMemo(() => {
    if (!document) return [];
    return document.sections
      .filter((section) => section.category === category)
      .filter((section) => !texts.some((text) => text.sectionKey === section.key))
      .sort((a, b) => a.order - b.order);
  }, [document, category, texts]);

  // O guia de seções convida a criar texto; entre os inativos ele estaria
  // respondendo a outra pergunta — ali a pessoa veio procurar o que sumiu.
  const showSectionGuide =
    !loading &&
    texts.length === 0 &&
    sectionsWithoutText.length > 0 &&
    status === StandardTextStatus.ATIVO;

  const emptyState = hasFilters
    ? {
        title: 'Nenhum texto encontrado para o filtro atual',
        description: 'Limpe os filtros para ver os textos do seu escopo.',
      }
    : {
        title: 'Nenhum texto padronizado cadastrado no seu escopo',
        description: 'É o texto padronizado que preenche as seções dos documentos emitidos — comece por "Novo Texto".',
      };

  return (
    <PageShell
      breadcrumbs={[
        { label: 'Início', path: '/' },
        { label: 'Textos Padronizados' },
      ]}
      title="Textos Padronizados"
      actions={
        <RouteActionButton
          para="/standard-texts/new"
          exige={pode('standard_text.create')}
          variant="contained"
          color="primary"
          startIcon={<AddIcon />}
        >
          Novo Texto
        </RouteActionButton>
      }
    >
      {error && !loading && (
        <Alert severity="error" sx={{ mb: 3 }}>
          {error}
        </Alert>
      )}

      {/* Filters */}
      <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} sx={{ mb: 3 }} alignItems="center">
        <TextField
          placeholder="Buscar por título ou categoria..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          onKeyDown={(e) => e.key === 'Enter' && handleSearch()}
          size="small"
          sx={{ minWidth: 300, maxWidth: 400 }}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon />
              </InputAdornment>
            ),
          }}
        />

        <FormControl size="small" sx={{ minWidth: 240 }}>
          <InputLabel id="standard-text-filter-category-label">Categoria</InputLabel>
          <Select
            value={category}
            labelId="standard-text-filter-category-label"
            label="Categoria"
            onChange={(event) => {
              setCategory(String(event.target.value));
              setPage(0);
            }}
          >
            <MenuItem value="">Todas as categorias</MenuItem>
            {categories.map((option) => (
              <MenuItem key={option.value} value={option.value}>
                {option.label}
              </MenuItem>
            ))}
          </Select>
        </FormControl>

        <FormControl size="small" sx={{ minWidth: 180 }}>
          <InputLabel id="standard-text-filter-status-label">Status</InputLabel>
          <Select
            value={status}
            labelId="standard-text-filter-status-label"
            label="Status"
            onChange={(event) => {
              setStatus(event.target.value as StandardTextStatus);
              setPage(0);
            }}
          >
            <MenuItem value={StandardTextStatus.ATIVO}>Ativos</MenuItem>
            <MenuItem value={StandardTextStatus.INATIVO}>Inativos</MenuItem>
          </Select>
        </FormControl>

        {hasFilters && (
          <Button variant="text" onClick={handleClearFilters}>
            Limpar filtros
          </Button>
        )}
      </Stack>

      {showSectionGuide ? (
        <Card>
          <CardContent>
            <Typography variant="subtitle1" gutterBottom>
              {`Nenhum texto para ${document?.label ?? 'este documento'} no seu escopo.`}
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              Cada seção do documento pode ter texto próprio. Enquanto não tiver, a emissão usa o
              texto genérico da categoria ou o conteúdo padrão do sistema. Seções ainda sem texto
              próprio:
            </Typography>

            <Stack spacing={1}>
              {sectionsWithoutText.map((section) => (
                <Box
                  key={section.key}
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    gap: 2,
                    flexWrap: 'wrap',
                  }}
                >
                  <Typography variant="body2">{section.title}</Typography>
                  <RouteActionButton
                    para="/standard-texts/new"
                    exige={pode('standard_text.create')}
                    size="small"
                    variant="outlined"
                    startIcon={<AddIcon />}
                    aoAgir={() =>
                      navigate(
                        `/standard-texts/new?category=${section.category}&sectionKey=${section.key}`
                      )
                    }
                  >
                    Criar texto desta seção
                  </RouteActionButton>
                </Box>
              ))}
            </Stack>

          </CardContent>
        </Card>
      ) : (
        <DataTable
          columns={textColumns}
          rows={texts}
          getRowId={(text) => text.id}
          loading={loading}
          emptyState={emptyState}
          actions={[
            {
              icon: <EditIcon fontSize="small" />,
              onClick: (text) => navigate(`/standard-texts/${text.id}/edit`),
              ...editar,
            },
            {
              icon: <DeleteIcon fontSize="small" />,
              color: 'error',
              onClick: (text) => handleDelete(text.id),
              hidden: (text) => text.status !== StandardTextStatus.ATIVO,
              ...excluir,
            },
            {
              icon: <RestoreIcon fontSize="small" />,
              color: 'success',
              onClick: (text) => handleReactivate(text.id),
              hidden: (text) => text.status === StandardTextStatus.ATIVO,
              tooltip: reativar.tooltip,
              disabled: reativar.disabled || reactivating !== null,
            },
          ]}
          pagination={{
            page,
            rowsPerPage,
            totalCount: total,
            onPageChange: setPage,
            onRowsPerPageChange: (value) => {
              setRowsPerPage(value);
              setPage(0);
            },
          }}
        />
      )}
    </PageShell>
  );
};

export default StandardTextListPage;
